import { NFTI } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PreloadImage } from 'src/features/common/app/components/preload-image';
import { NftCard } from 'src/features/common/collectible/components/nft-card';
import { useFetchDataForNftCards } from 'src/features/common/collection/hooks/use-fetch-data-for-nft-cards';
import { ExploreItem } from 'src/features/common/explore/containers/nfts-explore-container/explore-item';
import { useCalculateExploreItemHeight } from 'src/features/common/explore/hooks/use-calculate-explore-item-height';
import {
    useFilters,
    useNftsByPage,
    useNftsEntities,
    usePages,
    useTotal,
} from 'src/features/common/explore/hooks/use-explore-nfts';
import { useInfiniteExploreTokens } from 'src/features/common/explore/hooks/use-infinite-explore-tokens';
import { ItemPlaceholder } from 'src/features/common/shared/components/item-placeholder';
import { NftCard as NftNinjaCard } from 'src/features/white-labels/ninja/collectible/components/nft-card';
import { NftCard as NftCardOceanTemplate } from 'src/features/white-labels/ocean/collectible/components/nft-card';
import { useDebouncedEffect } from 'src/hooks/use-debounced-effect';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { Grid } from 'src/ui/common/atoms/grid';
import { useExploreComputedItems } from '../../hooks/use-explore-computed-items';
import { StyledLoadMoreButton } from './styles';

interface LocaleI {
    loadMore: string;
    emptyTitle: string;
}

const INITIAL_EXPLORE_ITEM_HEIGHT = '400px';
export const NftsExploreContainer = ({
    sideBarOpened,
    emptyPlaceholder: EmptyPlaceholder,
}: {
    sideBarOpened: boolean;
    emptyPlaceholder: React.FC<{ itemsGridConfig: any }>;
}) => {
    const { t } = useTranslation(['actions']);
    const screen = useScreenDetect();
    const filters = useFilters();
    const fetchDataForNftCards = useFetchDataForNftCards();
    const wrapperRef = useRef() as React.MutableRefObject<HTMLDivElement>;
    const { isOceanTemplate, isNinjaTemplate } = useGetUniverseTemplate();

    const [infinityScrollEnabled, setInfinityScrollEnabled] = useState(false);

    const { itemHeight, calculateItemHeight } = useCalculateExploreItemHeight({
        selector: 'data-test-id=infinite-explore-item',
        initialHeight: INITIAL_EXPLORE_ITEM_HEIGHT,
    });

    const locale: LocaleI = useMemo(
        () => ({
            loadMore: t('misc.loadMore', { ns: 'actions' }),
            emptyTitle: t('emptyList.nft.title', { ns: 'components' }),
        }),
        [t]
    );

    const { fetch, loading, total, pager } = useInfiniteExploreTokens({ limit: 20 });

    const items = useExploreComputedItems<NFTI>({
        limit: 20,
        useEntities: useNftsEntities,
        useByPage: useNftsByPage,
        useTotal,
        usePages,
    });

    const visibleItems = useMemo(() => {
        let visibleCount = 8;
        if (screen.sm) {
            visibleCount = sideBarOpened ? 8 : 16;
        }
        if (screen.md) {
            visibleCount = sideBarOpened ? 12 : 16;
        }
        if (screen.lg) {
            visibleCount = sideBarOpened ? 12 : 15;
        }
        if (screen.xl) {
            visibleCount = sideBarOpened ? 15 : 18;
        }
        if (infinityScrollEnabled || total < visibleCount || pager?.page) {
            return items;
        }
        // TODO
        return items.slice(0, visibleCount);
    }, [infinityScrollEnabled, items, total, screen, pager, sideBarOpened]);

    const loadMore = useCallback(() => {
        if (!loading) {
            fetch({ page: pager.page! + 1 });
        }
    }, [pager.page, loading, fetch]);

    const onFiltersChange = useCallback(() => {
        fetch({ page: 0 });
    }, [fetch]);

    const itemsGridConfig = useMemo(
        () => ({
            xs: 2,
            sm: sideBarOpened ? 2 : 4,
            md: sideBarOpened ? 3 : 4,
            lg: sideBarOpened ? 4 : 5,
            xl: sideBarOpened ? 5 : 6,
        }),
        [sideBarOpened]
    );

    useDebouncedEffect(onFiltersChange, 50, [filters]);

    useInfiniteScroll({
        cb: loadMore,
        disabled: !infinityScrollEnabled,
        threshold: 300,
    });

    useEffect(() => {
        calculateItemHeight();
    }, [calculateItemHeight, total, items]);

    useEffect(() => {
        const mutationObserver = new MutationObserver(calculateItemHeight);
        const resizeObserver = new ResizeObserver(calculateItemHeight);
        if (wrapperRef.current) {
            resizeObserver.observe(wrapperRef.current);
            mutationObserver.observe(wrapperRef.current, {
                childList: true,
                subtree: true,
            });
        }
        return () => {
            if (wrapperRef.current) {
                mutationObserver.disconnect();
                resizeObserver.unobserve(wrapperRef.current);
            }
        };
    }, [calculateItemHeight]);

    useEffect(() => {
        // @ts-ignore
        fetchDataForNftCards(visibleItems.filter(Boolean));
    }, [fetchDataForNftCards, visibleItems]);

    const getNftCard = useCallback(
        (item) => {
            if (isNinjaTemplate) {
                return <NftNinjaCard nft={item} testId="infinite-explore-item" />;
            }
            if (isOceanTemplate) {
                return <NftCardOceanTemplate nft={item} testId="infinite-explore-item" />;
            }

            return <NftCard nft={item} testId="infinite-explore-item" />;
        },
        [isNinjaTemplate, isOceanTemplate]
    );

    return (
        <div ref={wrapperRef}>
            {visibleItems[0] && <PreloadImage src={visibleItems[0]?.previewAssets?.webps?.mediumUrl!} />}
            {!loading && !+total && <EmptyPlaceholder itemsGridConfig={itemsGridConfig} />}
            <Grid {...itemsGridConfig} rowGap={10} columnGap={10} rowGapSm={20} columnGapLg={15}>
                {visibleItems.map((item, index) => (
                    <ExploreItem height={itemHeight} key={item?.id ?? index}>
                        {item ? getNftCard(item) : <ItemPlaceholder />}
                    </ExploreItem>
                ))}
            </Grid>
            {!infinityScrollEnabled && total > visibleItems.length && (
                <StyledLoadMoreButton
                    stretch
                    disabled={loading}
                    onClick={() => setInfinityScrollEnabled(true)}
                    variant={isNinjaTemplate ? 'rounded' : 'grey'}
                >
                    {locale.loadMore}
                </StyledLoadMoreButton>
            )}
        </div>
    );
};